// The organization: who it can bid as (eligibility profile), and its people and seats.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { api, errorText } from "../api/client";
import { useUI } from "../app/context";
import { keys, useSession } from "../app/queries";
import { Face } from "../components/Badge";

interface Member {
  id: number;
  name: string;
  email: string;
  role: "admin" | "member";
  disabled: boolean;
  active: boolean;
  initials: string;
}
interface MembersResponse {
  tenant: { id: number; name: string; seats: number; used: number };
  members: Member[];
  can_manage: boolean;
}

export function Members() {
  const me = useSession();
  const client = useQueryClient();
  const { showToast } = useUI();
  const members = useQuery({ queryKey: ["members"], queryFn: () => api<MembersResponse>("/api/members") });
  const [link, setLink] = useState<{ who: string; url: string | null } | null>(null);
  const [form, setForm] = useState({ email: "", name: "", role: "member" });
  const refresh = () => client.invalidateQueries({ queryKey: ["members"] });
  const act = async (fn: () => Promise<unknown>, done?: string) => {
    try {
      await fn();
      if (done) showToast(done);
    } catch (err) {
      showToast(errorText(err));
    }
    await refresh();
  };
  if (!members.data) return <p className="empty">Loading…</p>;
  const d = members.data;
  const t = d.tenant;
  const pct = Math.min(100, Math.round((100 * t.used) / Math.max(1, t.seats)));
  const add = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const r = await api<{ id: number; invite_url: string | null }>("/api/members", { method: "POST", body: form });
      setLink({ who: form.name, url: r.invite_url });
      setForm({ email: "", name: "", role: "member" });
      await refresh();
    } catch (err) {
      showToast(errorText(err));
    }
  };
  return (
    <div className="admin">
      <div className="top">
        <div className="date">{t.name}</div>
      </div>
      <h1>{t.name}</h1>
      <OrgProfile />
      <h2 className="org-h">People and seats</h2>
      <p className="lede">
        <b>
          {t.used} of {t.seats} seats
        </b>{" "}
        in use.
        <span className="meter" aria-hidden="true">
          <i style={{ width: `${pct}%` }} />
        </span>
        {d.can_manage ? null : " Ask an admin to add people."}
      </p>
      {d.can_manage ? (
        <>
          <form className="inline" id="add-member" onSubmit={add}>
            <label>
              <span>Email</span>
              <input type="email" id="am-email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </label>
            <label>
              <span>Name</span>
              <input id="am-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </label>
            <label>
              <span>Role</span>
              <select id="am-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </label>
            <button className="btn primary" disabled={t.used >= t.seats} title={t.used >= t.seats ? "All seats are in use" : undefined}>
              Add person
            </button>
          </form>
          <div id="invite-out">
            {link ? (
              <div className="link-box">
                {link.url ? (
                  <>
                    Send {link.who} this one-time link to set a password (valid 7 days):
                    <br />
                    <code>{link.url}</code>{" "}
                    <button className="btn" id="copy-link" type="button" onClick={() => navigator.clipboard.writeText(link.url!).then(() => showToast("Link copied."))}>
                      Copy
                    </button>
                  </>
                ) : (
                  `${link.who} already has a GovChew account and can sign in with their existing password.`
                )}
              </div>
            ) : null}
          </div>
        </>
      ) : null}
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Status</th>
            {d.can_manage ? <th /> : null}
          </tr>
        </thead>
        <tbody>
          {d.members.map((m) => (
            <tr key={m.id}>
              <td>
                <Face id={m.id} text={m.initials} />
                {m.name}
              </td>
              <td>{m.email}</td>
              <td>
                {d.can_manage && m.id !== me.user.id ? (
                  <select
                    data-role={m.id}
                    aria-label={`Role for ${m.name}`}
                    value={m.role}
                    onChange={(e) => act(() => api(`/api/members/${m.id}`, { method: "PATCH", body: { role: e.target.value } }), "Role updated.")}
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </select>
                ) : m.role === "admin" ? (
                  "Admin"
                ) : (
                  "Member"
                )}
              </td>
              <td>{m.disabled ? "Disabled" : m.active ? "Active" : "Invited"}</td>
              {d.can_manage ? (
                <td className="actions">
                  {m.id !== me.user.id ? (
                    <>
                      <button
                        className="btn"
                        data-invite={m.id}
                        onClick={async () => {
                          try {
                            const r = await api<{ invite_url: string }>(`/api/members/${m.id}/invite`, { method: "POST" });
                            setLink({ who: m.name, url: r.invite_url });
                          } catch (err) {
                            showToast(errorText(err));
                          }
                        }}
                      >
                        {m.active ? "Reset link" : "New invite link"}
                      </button>{" "}
                      <button
                        className="btn danger"
                        data-remove={m.id}
                        onClick={() => {
                          if (!confirm(`Remove ${m.name} from ${t.name}? This frees their seat and signs them out here. Feeds they own stay with the organization.`))
                            return;
                          act(() => api(`/api/members/${m.id}`, { method: "DELETE" }), `Removed ${m.name}.`);
                        }}
                      >
                        Remove
                      </button>
                    </>
                  ) : null}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
      {me.superuser ? (
        <p style={{ color: "var(--ink-3)", marginTop: 16 }}>
          As a superuser you can manage any organization; you only take a seat in the ones you are a member of.
        </p>
      ) : null}
    </div>
  );
}

interface Profile {
  profile: { size: string; statuses: string[] };
  sizes: Record<string, string>;
  statuses: Record<string, string>;
  can_manage: boolean;
}

function OrgProfile() {
  const client = useQueryClient();
  const { showToast } = useUI();
  const profile = useQuery({ queryKey: ["organization", "profile"], queryFn: () => api<Profile>("/api/organization/profile") });
  const save = useMutation({
    mutationFn: (p: { size: string; statuses: string[] }) => api("/api/organization/profile", { method: "PUT", body: p }),
    onSuccess: async () => {
      showToast("Saved. Feeds now hide set-asides you can’t bid on.");
      await Promise.all([
        client.invalidateQueries({ queryKey: ["organization", "profile"] }),
        client.invalidateQueries({ queryKey: keys.feeds }),
        client.invalidateQueries({ queryKey: ["feed"] }),
        client.invalidateQueries({ queryKey: keys.brief }),
      ]);
    },
    onError: (err) => showToast(errorText(err)),
  });
  if (!profile.data) return <section className="org-profile" id="org-profile" />;
  const d = profile.data;
  const p = d.profile;
  const disabled = !d.can_manage;
  return (
    <section className="org-profile" id="org-profile">
      <h2 className="org-h">Who we can bid as</h2>
      <p className="muted-s">
        Set-asides your organization can't bid on, and grants open only to universities, nonprofits and governments, are hidden from every feed and the
        daily brief. Search still finds them, marked. Leave business size unspecified to hide nothing.
      </p>
      <form id="profile-form" onSubmit={(e) => e.preventDefault()}>
        <fieldset className="inline-radios">
          <legend>Business size</legend>
          {Object.entries(d.sizes).map(([v, l]) => (
            <label key={v}>
              <input type="radio" name="size" value={v} checked={p.size === v} disabled={disabled} onChange={() => save.mutate({ size: v, statuses: p.statuses })} /> {l}
            </label>
          ))}
        </fieldset>
        <fieldset className="checks" id="statuses" hidden={p.size !== "small"}>
          <legend>Certifications (small businesses)</legend>
          {Object.entries(d.statuses).map(([v, l]) => (
            <label key={v}>
              <input
                type="checkbox"
                name="status"
                value={v}
                checked={p.statuses.includes(v)}
                disabled={disabled}
                onChange={(e) =>
                  save.mutate({ size: p.size, statuses: e.target.checked ? [...p.statuses, v] : p.statuses.filter((s) => s !== v) })
                }
              />{" "}
              {l}
            </label>
          ))}
        </fieldset>
        {d.can_manage ? null : <p className="muted-s">Only an admin can change this.</p>}
      </form>
    </section>
  );
}
